import React, { useEffect, useState } from 'react';
import { Sorting } from 'src/types/Sorting';
import SORT_ORDER from 'src/enums/sortOrder';
import { ProxyCredential } from 'src/types/ProxyServiceInfo';
import { useGetServiceList } from 'src/api/proxyService';
import ServiceTable from './ServiceTable';
import EditUserServiceModal from './EditUserServiceModal';
import EditConfigurationServiceModal from './EditConfigurationServiceModal';
import styles from './ServiceList.module.less';

export type ServiceListProps = {
  proxyServiceName?: string;
  enabled?: string;
  ipErrorHandle?: string;
  requestParams: ProxyCredential;
  setRequestParams: (newParams: ProxyCredential) => void;
};

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_NUMBER = 0;

const ServiceList: React.FC<ServiceListProps> = ({
  proxyServiceName,
  enabled,
  ipErrorHandle,
  requestParams,
  setRequestParams,
}: ServiceListProps): React.ReactElement => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(PAGE_NUMBER);
  const [currentColumn] = useState('proxyServiceName');
  const [currentOrder] = useState('ascend');
  const [sorting, setSorting] = useState({ field: currentColumn, order: currentOrder } as Sorting<ProxyCredential>);
  const { loading, totalRecords, proxyServiceList } = useGetServiceList(requestParams);
  const [editUserService, setEditUserService] = useState<ProxyCredential | null>(null);
  const [editConfigurationService, setEditConfigurationService] = useState<ProxyCredential | null>(null);
  const [editEnable, setEditEnable] = useState<ProxyCredential | null>(null);
  const [editUserVisible, setEditUserVisible] = useState(false);
  const [editConfigurationVisible, setEditConfigurationVisible] = useState(false);

  const getOffset = (page: number, size: number): number => {
    if (
      requestParams &&
      ((requestParams.proxyServiceName && requestParams.proxyServiceName !== proxyServiceName) ||
        (!requestParams.proxyServiceName && proxyServiceName) ||
        (requestParams.size && requestParams.size !== pageSize) ||
        requestParams.enabled !== enabled ||
        requestParams.ipErrorHandle !== ipErrorHandle)
    ) {
      setPage(PAGE_NUMBER);
      return PAGE_NUMBER;
    }

    return page > 1 ? (page - 1) * size : PAGE_NUMBER;
  };

  const onUpdateList = () => {
    setEditUserService(null);
    if (page === 0) {
      setRequestParams({
        ...requestParams,
        enabled,
        ipErrorHandle,
        proxyServiceName,
        pageSize,
      });
    } else {
      const newPage = page > 1 ? page - 1 : PAGE_NUMBER;
      setPage(newPage);
    }
  };

  useEffect(() => {
    let newParams: any = {
      enabled: enabled,
      ipErrorHandle: ipErrorHandle,
      size: pageSize,
      offset: getOffset(page, pageSize),
    };
    if (sorting) {
      newParams = {
        ...newParams,
        sortingorder: SORT_ORDER[sorting.order],
        sortingcolumn: sorting.field,
      };
    }
    if (proxyServiceName) {
      newParams = { ...newParams, proxyServiceName };
    }
    setRequestParams(newParams);
  }, [proxyServiceName, enabled, ipErrorHandle, page, pageSize, sorting, editEnable]);

  const onSortingChange = (sorting: Sorting<ProxyCredential>): void => {
    setSorting(sorting);
  };

  const onPageSizeChange = (pageSize: number): void => {
    setPageSize(pageSize);
  };

  const onPageChange = (page: number): void => {
    setPage(page);
  };

  const onAction = (record: ProxyCredential, type: string) => {
    if (type === 'editUserService') {
      setEditUserService(record);
      setEditUserVisible(true);
    }
    if (type === 'editConfigurationService') {
      setEditConfigurationService(record);
      setEditConfigurationVisible(true);
    }
  };

  const onChangeStatus = (record: ProxyCredential | null) => {
    setEditEnable(record);
  };

  return (
    <div className={styles.serviceListTableWrapper}>
      <ServiceTable
        items={proxyServiceList}
        sorting={sorting}
        loading={loading}
        pageSize={pageSize}
        page={page}
        total={totalRecords ? parseInt(totalRecords.toString(), 10) : 0}
        onPageSizeChange={onPageSizeChange}
        onPageChange={onPageChange}
        onSortingChange={onSortingChange}
        onAction={onAction}
        onChangeStatus={onChangeStatus}
      />
      <EditUserServiceModal
        visible={editUserVisible}
        setVisible={setEditUserVisible}
        userService={editUserService}
        onUpdate={onUpdateList}
        onEdit={setEditUserService}
      />
      <EditConfigurationServiceModal
        visible={editConfigurationVisible}
        setVisible={setEditConfigurationVisible}
        proxyService={editConfigurationService}
        onUpdate={onUpdateList}
        onEdit={setEditConfigurationService}
      />
    </div>
  );
};

export default ServiceList;
